// ---------------------------------------------------------------------------
// PeopleMosaic -- Avatar grid strip with depth effect
// ---------------------------------------------------------------------------

import React from 'react';
import { cn } from '@/lib/utils';
import { getPlatformIcon } from '@/components/v11/shared/platformIcons';
import type { AudienceProfile } from './types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PeopleMosaicProps {
  readonly profiles: readonly AudienceProfile[];
  readonly totalCount: number;
  readonly className?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getInitials(username: string): string {
  return username.slice(0, 2).toUpperCase();
}

const GRADIENT_COLORS = [
  'from-blue-500 to-cyan-500',
  'from-purple-500 to-pink-500',
  'from-green-500 to-emerald-500',
  'from-orange-500 to-red-500',
  'from-indigo-500 to-violet-500',
  'from-teal-500 to-green-500',
  'from-rose-500 to-pink-500',
  'from-amber-500 to-yellow-500',
];

function getGradient(index: number): string {
  return GRADIENT_COLORS[index % GRADIENT_COLORS.length];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function PeopleMosaic({ profiles, totalCount, className }: PeopleMosaicProps) {
  // Split into 3 rows for depth effect
  const row1 = profiles.slice(0, 20);
  const row2 = profiles.slice(20, 37);
  const row3 = profiles.slice(37, 50);

  const remaining = totalCount - profiles.length;

  return (
    <div
      className={cn(
        'relative rounded-xl border border-v11-border bg-v11-surface p-4 overflow-visible',
        className
      )}
    >
      {/* Label */}
      <div className="text-xs uppercase tracking-wider text-v11-text-muted mb-3 font-semibold">
        Your Audience
      </div>

      {/* Row 1 - Full size */}
      <div className="flex gap-1.5 mb-1.5 overflow-visible">
        {row1.map((profile, i) => (
          <AvatarCircle key={profile.id} profile={profile} size="md" index={i} />
        ))}
      </div>

      {/* Row 2 - Medium with remaining badge */}
      <div className="flex gap-1 mb-1 overflow-visible items-center">
        {row2.map((profile, i) => (
          <AvatarCircle key={profile.id} profile={profile} size="sm" index={i + 20} />
        ))}
        {remaining > 0 && (
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-v11-surface-hover border border-v11-border flex items-center justify-center">
            <span className="text-[9px] font-bold text-v11-text-muted">
              +
              {remaining > 999999
                ? `${Math.round(remaining / 1000)}K`
                : remaining > 999
                  ? `${Math.round(remaining / 1000)}K`
                  : remaining}
            </span>
          </div>
        )}
      </div>

      {/* Row 3 - Small, fading */}
      <div className="flex gap-0.5 overflow-hidden opacity-50">
        {row3.map((profile, i) => (
          <AvatarCircle key={profile.id} profile={profile} size="xs" index={i + 37} />
        ))}
      </div>

      {/* Fade overlay */}
      <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-v11-surface to-transparent pointer-events-none" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Avatar Circle
// ---------------------------------------------------------------------------

interface AvatarCircleProps {
  readonly profile: AudienceProfile;
  readonly size: 'md' | 'sm' | 'xs';
  readonly index: number;
}

const SIZE_CLASSES = {
  md: 'w-10 h-10 text-xs',
  sm: 'w-8 h-8 text-[10px]',
  xs: 'w-6 h-6 text-[8px]',
} as const;

function formatFollowers(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function AvatarCircle({ profile, size, index }: AvatarCircleProps) {
  const sizeClass = SIZE_CLASSES[size];
  const showTooltip = size !== 'xs';

  // Position within the current row (0-based) to detect edge avatars
  const rowIndex = index < 20 ? index : index < 37 ? index - 20 : index - 37;
  const isLeftEdge = rowIndex < 2;

  return (
    <div
      className={cn(
        'flex-shrink-0 rounded-full overflow-visible relative',
        showTooltip && 'group/avatar cursor-pointer'
      )}
    >
      <div
        className={cn(
          'rounded-full overflow-hidden transition-transform',
          sizeClass,
          showTooltip &&
            'group-hover/avatar:scale-110 group-hover/avatar:ring-2 group-hover/avatar:ring-blue-500/50'
        )}
      >
        {profile.profile_pic_url ? (
          <img
            src={profile.profile_pic_url}
            alt={profile.username}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div
            className={cn(
              'w-full h-full flex items-center justify-center font-bold text-white bg-gradient-to-br',
              getGradient(index)
            )}
          >
            {getInitials(profile.username)}
          </div>
        )}
      </div>

      {/* Tooltip - left-aligned for edge avatars, centered otherwise */}
      {showTooltip && (
        <div
          className={cn(
            'absolute bottom-full mb-1.5 opacity-0 pointer-events-none group-hover/avatar:opacity-100 transition-opacity duration-200 z-50',
            isLeftEdge ? 'left-0' : 'left-1/2 -translate-x-1/2'
          )}
        >
          <div className="bg-v11-bg border border-v11-border rounded-lg shadow-xl px-2.5 py-1.5 whitespace-nowrap">
            <div className="text-[11px] font-semibold text-v11-text-primary">
              @{profile.username}
            </div>
            <div className="text-[10px] text-v11-text-muted tabular-nums">
              {formatFollowers(profile.followers_count)} followers
              {profile.platform_name &&
                (() => {
                  const pi = getPlatformIcon(profile.platform_name);
                  return (
                    <span className="ml-1 inline-flex items-center gap-0.5 text-v11-text-muted">
                      ·{' '}
                      {pi && (
                        <span className="[&>svg]:h-2.5 [&>svg]:w-2.5" style={{ color: pi.color }}>
                          {pi.icon}
                        </span>
                      )}
                      {profile.platform_name}
                    </span>
                  );
                })()}
            </div>
            {/* Arrow pointer - aligned to match tooltip position */}
            <div
              className={cn(
                'absolute -bottom-1 w-2 h-2 rotate-45 bg-v11-bg border-r border-b border-v11-border',
                isLeftEdge ? 'left-4' : 'left-1/2 -translate-x-1/2'
              )}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export { PeopleMosaic };
export type { PeopleMosaicProps };
